// reset-mode.js — pure helpers for Reset Mode (bulk pantry-audit triage).
//
// Reset Mode is NOT the everyday add-one-item scanner (that already lives on the
// Pantry tab). It's a session-based *triage* flow: sweep a shelf, and give every
// scanned/typed item a Keep / Use soon / Toss decision, then review + commit the
// whole batch at once. This module owns the pure decision → data mapping so the
// UI (src/modules/Reset.jsx) and the store method (commitResetItem) stay thin.
//
// How the three decisions map onto Pronto's existing data model (open-questions §G):
//   • Keep     → a normal pantry_inventory insert (session location + qty + best-by).
//   • Use soon → same insert, but forced into the Use-It-or-Lose-It window (Phase 5)
//                by defaulting expiry to the category's use-soon lead when the user
//                gives no best-by, so it surfaces immediately as "use soon".
//   • Toss     → NOT kept; written to shopping_list as a replace item (Phase 3).

import { USE_SOON_LEAD_DAYS } from "./pantry";

// The physical-location picker for a reset session. Stored lowercased on the
// inventory row's `location` (free-text column) — "Fridge"→"fridge",
// "Freezer"→"freezer" line up with the canonical storage values already in use.
export const RESET_LOCATIONS = ["Pantry", "Fridge", "Freezer", "Spice rack", "Counter", "Other"];

export function canonicalLocation(label) {
  return String(label || "Pantry").trim().toLowerCase();
}

export const TRIAGE = { KEEP: "keep", SOON: "soon", TOSS: "toss" };
export const TRIAGE_LABELS = { keep: "Keep", soon: "Use soon", toss: "Toss" };

// Resolve the expires_at for a kept/soon item.
//   • an explicit best-by (a <input type="date"> "YYYY-MM-DD" value) always wins;
//   • "Use soon" with no best-by → now + the category's use-soon lead, so the item
//     lands in the Use-It-or-Lose-It surface right away;
//   • otherwise null → caller falls back to newInventoryEntryForSku's category
//     default shelf life.
// Returns an ISO string or null. `now` is injectable for tests.
export function resetExpiry({ triage, bestBy, category = "other" }, now = new Date()) {
  if (bestBy) {
    const d = new Date(`${bestBy}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  if (triage === TRIAGE.SOON) {
    const lead = USE_SOON_LEAD_DAYS[category] ?? USE_SOON_LEAD_DAYS.other;
    return new Date(now.getTime() + lead * 86400000).toISOString();
  }
  return null;
}

// Running session tallies for the header counter. "Kept" = anything not tossed
// (keep + soon), matching the prototype's chip semantics.
export function sessionCounts(items) {
  const list = items || [];
  let kept = 0;
  let toss = 0;
  for (const i of list) {
    if (i.triage === TRIAGE.TOSS) toss++;
    else kept++;
  }
  return { kept, toss, total: list.length };
}

// Review ordering: toss first (most actionable), then use-soon, then keep.
const REVIEW_ORDER = { toss: 0, soon: 1, keep: 2 };
export function sortForReview(items) {
  return (items || []).slice().sort((a, b) => REVIEW_ORDER[a.triage] - REVIEW_ORDER[b.triage]);
}

// A short "×qty · location · best by … · brand" line for a review row.
export function itemMetaLine(item) {
  const bits = [];
  if (item.qty > 1) bits.push(`×${item.qty}`);
  if (item.location) bits.push(item.location);
  if (item.bestBy) bits.push(`best by ${item.bestBy}`);
  if (item.meta) bits.push(item.meta);
  return bits.join(" · ");
}

// ---- Exports (CSV + toss list), ported from the prototype ----

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildResetCsv(items) {
  const rows = [["Item", "Qty", "Location", "Decision", "Best by", "Barcode", "Details"]];
  for (const i of items || []) {
    rows.push([i.name, i.qty, i.location, TRIAGE_LABELS[i.triage] || i.triage, i.bestBy || "", i.barcode || "", i.meta || ""]);
  }
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}

// The "copy toss list" text — tossed items as a replace/shopping list.
export function tossListText(items) {
  const toss = (items || []).filter((i) => i.triage === TRIAGE.TOSS);
  if (!toss.length) return "";
  return (
    "Replace (tossed during pantry reset):\n" +
    toss.map((i) => `- ${i.name}${i.qty > 1 ? ` ×${i.qty}` : ""}  [${i.location}]`).join("\n")
  );
}

// Trigger a client-side file download (CSV export). No-op outside the browser.
export function downloadFile(name, text, mime = "text/plain") {
  if (typeof document === "undefined") return;
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 400);
}
